import { lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router-dom";
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  AuthenticateWithRedirectCallback,
} from "@clerk/clerk-react";
import { dark } from "@clerk/themes";
import Layout from "./components/Layout";
import { ExtensionSync } from "./components/ExtensionSync";

// Lazy loading pages
const Welcome = lazy(() => import("./pages/Welcome"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Success = lazy(() => import("./pages/Success"));
const Cancel = lazy(() => import("./pages/Cancel"));
const About = lazy(() => import("./pages/About"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkFrontendApi = import.meta.env.VITE_CLERK_FRONTEND_API_URL?.replace(/^https?:\/\//, '');

if (import.meta.env.PROD) {
  const isLiveKey = clerkPubKey?.startsWith('pk_live_');
  console.log(`[Clerk] Running in PRODUCTION mode. Key is ${isLiveKey ? 'LIVE' : 'TEST (ERROR!)'}`);
}

if (!clerkPubKey) {
  throw new Error("Missing Publishable Key");
}

const LoadingFallback = () => (
  <div className="min-h-screen bg-brand-dark flex items-center justify-center">
    <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
  </div>
);

// Root Layout Component to wrap everything
const RootLayout = () => (
  <Layout>
    <ExtensionSync />
    <Suspense fallback={<LoadingFallback />}>
      <Outlet />
    </Suspense>
  </Layout>
);

// Router Configuration with View Transitions enabled
const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        path: "/about",
        element: <About />,
        // @ts-ignore - Supported in v7/Data Router
        viewTransition: true,
      },
      {
        path: "/privacy",
        element: <PrivacyPolicy />,
        // @ts-ignore
        viewTransition: true,
      },
      {
        path: "/terms",
        element: <TermsOfService />,
        // @ts-ignore
        viewTransition: true,
      },
      {
        path: "/sso-callback",
        element: <AuthenticateWithRedirectCallback signInUrl="/" />,
        // @ts-ignore
        viewTransition: true,
      },
      {
        path: "/",
        element: (
          <>
            <SignedIn>
              <Dashboard />
            </SignedIn>
            <SignedOut>
              <Welcome />
            </SignedOut>
          </>
        ),
        // @ts-ignore
        viewTransition: true,
      },
      {
        path: "/success",
        element: (
          <SignedIn>
            <Success />
          </SignedIn>
        ),
        // @ts-ignore
        viewTransition: true,
      },
      {
        path: "/cancel",
        element: (
          <SignedIn>
            <Cancel />
          </SignedIn>
        ),
        // @ts-ignore
        viewTransition: true,
      },
      {
        path: "*",
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);

export default function App() {
  return (
    <ClerkProvider 
      publishableKey={clerkPubKey} 
      frontendApi={clerkFrontendApi}
      appearance={{ baseTheme: dark }}
    >
      <RouterProvider router={router} />
    </ClerkProvider>
  );
}
